import { Client } from '@stomp/stompjs'
import type { AiSuggestionMessage, AuditProgressMessage } from './client'
import { getBffBaseUrl } from './client'

function getBffWsUrl(): string {
  // Derive ws:// from the same base URL the REST client uses so prod (https → wss)
  // and dev (http → ws) stay in sync without a separate env knob.
  const base = getBffBaseUrl()
  const wsScheme = base.startsWith('https://') ? 'wss://' : 'ws://'
  return wsScheme + base.replace(/^https?:\/\//, '') + '/ws'
}

let stompClient: Client | null = null

function getClient(): Client {
  if (!stompClient) {
    stompClient = new Client({
      brokerURL: getBffWsUrl(),
      reconnectDelay: 3000,
    })
    stompClient.activate()
  }
  return stompClient
}

function subscribeToTopic<T>(
  destination: string,
  onMessage: (msg: T) => void,
): () => void {
  const client = getClient()

  const subscribe = () => {
    const sub = client.subscribe(destination, frame => {
      const msg = JSON.parse(frame.body) as T
      onMessage(msg)
    })
    return () => sub.unsubscribe()
  }

  if (client.connected) {
    return subscribe()
  }

  // Wait for connection then subscribe
  let unsubFn: (() => void) | null = null
  const prevOnConnect = client.onConnect
  client.onConnect = frame => {
    prevOnConnect?.call(client, frame)
    unsubFn = subscribe()
  }

  return () => unsubFn?.()
}

export function subscribeToAudit(
  auditId: string,
  onMessage: (msg: AuditProgressMessage) => void,
): () => void {
  return subscribeToTopic<AuditProgressMessage>(`/topic/audit/${auditId}`, onMessage)
}

/** Anonymous-audit topic — no workspace check (entropy of UUIDv4 is the access token). */
export function subscribeToAnonymousAudit(
  auditId: string,
  onMessage: (msg: AuditProgressMessage) => void,
): () => void {
  return subscribeToTopic<AuditProgressMessage>(`/topic/anon-audit/${auditId}`, onMessage)
}

/** Phase 6: subscribe to AI suggestion frames for an in-flight audit. */
export function subscribeToAiSuggestions(
  auditId: string,
  onMessage: (msg: AiSuggestionMessage) => void,
): () => void {
  return subscribeToTopic<AiSuggestionMessage>(`/topic/audit/${auditId}/ai`, onMessage)
}

export function disconnectWs() {
  stompClient?.deactivate()
  stompClient = null
}
